import { supabase } from "@/integrations/supabase/client";
import type { DocumentExportData } from "./docmosisService";

export interface TailoredResume {
  id: string;
  user_id: string;
  title: string;
  content: string;
  job_title?: string;
  company_name?: string;
  job_description?: string;
  match_score?: number;
  original_match_score?: number;
  analysis_results?: any;
  original_resume_id?: string;
  file_name?: string;
  file_type?: string;
  word_document_url?: string;
  word_document_path?: string;
  parsed_resume_data?: any;
  created_at: string;
  updated_at: string;
  // User profile information
  user_name?: string;
  user_email?: string;
  user_display_name?: string;
}

export interface GroupedTailoredResume {
  originalResumeId: string;
  originalResumeTitle: string;
  tailoredVersions: TailoredResume[];
}

export class TailoredResumeService {
  static async getTailoredResumes(): Promise<TailoredResume[]> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    console.log('=== FETCHING TAILORED RESUMES WITH PROFILE DATA ===');
    console.log('Current user ID:', user.id);

    // First, get the user's profile data
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('display_name, email, first_name, last_name')
      .eq('id', user.id)
      .single();

    if (profileError) {
      console.error('Error fetching user profile:', profileError);
    } else {
      console.log('User profile data:', profileData);
    }

    const { data, error } = await supabase
      .from('tailored_documents')
      .select('*')
      .eq('user_id', user.id) // CRITICAL: Filter by current user only
      .eq('document_type', 'resume') // Only get resume documents
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching tailored resumes:', error);
      throw error;
    }

    console.log('Fetched tailored resumes count:', data?.length || 0);

    // Transform the data to include user profile information
    const userName = profileData?.display_name || 
                    `${profileData?.first_name || ''} ${profileData?.last_name || ''}`.trim() || 
                    profileData?.email?.split('@')[0] ||
                    'Current User';

    console.log('Computed user name:', userName);

    return (data || []).map((resume: any) => ({
      ...resume,
      user_name: userName,
      user_email: profileData?.email,
      user_display_name: profileData?.display_name
    }));
  }

  static async getGroupedTailoredResumes(): Promise<GroupedTailoredResume[]> {
    const tailoredResumes = await this.getTailoredResumes();
    
    // Group resumes by original resume ID
    const groups = new Map<string, TailoredResume[]>();
    
    tailoredResumes.forEach(resume => {
      const originalId = resume.original_resume_id || 'unknown';
      if (!groups.has(originalId)) {
        groups.set(originalId, []);
      }
      groups.get(originalId)!.push(resume);
    });
    
    // Convert to GroupedTailoredResume array
    return Array.from(groups.entries()).map(([originalId, versions]) => {
      // Find the original resume title from the first version
      const originalTitle = versions.length > 0 
        ? versions[0].file_name || versions[0].title.replace(/\s*\(.*?\)\s*$/, '') || 'Original Resume'
        : 'Original Resume';
      
      return {
        originalResumeId: originalId,
        originalResumeTitle: originalTitle,
        tailoredVersions: versions.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      };
    });
  }

  private static extractPersonName(content: string): string {
    const lines = content.split('\n').map(line => line.trim()).filter(line => line.length > 0);
    // The first line typically contains the person's name
    return lines.length > 0 ? lines[0] : 'Resume';
  }

  static async exportSingleResumeToWord(resume: TailoredResume): Promise<void> {
    try {
      // Transform tailored resume to document export format
      const documentData: DocumentExportData = {
        id: resume.id,
        type: 'resume',
        title: resume.title || 'Tailored Resume',
        content: resume.content,
        jobTitle: resume.job_title,
        company: resume.company_name
      };

      // Use DocmosisService for proper export with correct naming
      const { DocmosisService } = await import('./docmosisService');
      const blob = await DocmosisService.exportToWord([documentData]);
      
      const filename = resume.file_name || `${resume.title || 'Resume'}.docx`;
      
      DocmosisService.downloadBlob(blob, filename);
      
    } catch (error) {
      console.error('Error exporting single resume:', error);
      throw error;
    }
  }

  static async exportBulkResumesToWord(resumes: TailoredResume[]): Promise<void> {
    try {
      const validResumes = resumes.filter(resume => resume.content);
      
      if (validResumes.length === 0) {
        throw new Error('No resumes available for export.');
      }

      const { DocmosisService } = await import('./docmosisService');
      
      // Process each resume individually to download separately with proper naming
      const exportPromises = validResumes.map(async (resume, index) => {
        try {
          // Transform tailored resume to document export format
          const documentData: DocumentExportData = {
            id: resume.id,
            type: 'resume',
            title: resume.title || 'Tailored Resume',
            content: resume.content,
            jobTitle: resume.job_title,
            company: resume.company_name
          };

          const blob = await DocmosisService.exportToWord([documentData]);
          
          const filename = resume.file_name || `${resume.title || 'Resume'}.docx`;
          
          // Delay to avoid overwhelming the browser
          setTimeout(() => {
            DocmosisService.downloadBlob(blob, filename);
          }, index * 500); // 500ms delay between downloads

        } catch (error) {
          console.error(`Error exporting resume ${resume.title}:`, error);
          throw error;
        }
      });

      await Promise.all(exportPromises);
      
    } catch (error) {
      console.error('Error in bulk export:', error);
      throw error;
    }
  }

  private static downloadFileFromBase64(filename: string, base64Data: string): void {
    try {
      // Decode base64 to bytes
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      
      // Create blob and download
      const blob = new Blob([byteArray], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', filename, error);
      throw new Error(`Failed to download ${filename}`);
    }
  }

  static async updateResumeJsonData(id: string, jsonData: any): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { error } = await supabase
      .from('resumes')
      .update({ parsed_resume_data: jsonData })
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) {
      console.error('Error updating resume JSON data:', error);
      throw error;
    }
  }

  /**
   * Save the original "before" resume during tailoring process
   * This ensures uploaded resumes don't appear in the main resume library
   */
  static async saveBeforeResume(data: {
    title: string;
    content: string;
    job_title?: string;
    company_name?: string;
    job_description?: string;
    file_name?: string;
    file_type?: string;
    original_file_url?: string;
    original_file_path?: string;
  }): Promise<TailoredResume> {
    const { data: result, error } = await supabase
      .from('resumes')
      .insert({
        ...data,
        user_id: (await supabase.auth.getUser()).data.user?.id,
        // Mark as tailored "before" version with minimal score to identify it
        original_match_score: 0, // This ensures it appears in tailored library, not regular library
        match_score: 0,
        title: `${data.title} (Before Tailoring)`,
      })
      .select()
      .single();

    if (error) {
      console.error('Error saving before resume:', error);
      throw error;
    }

    return result;
  }

  static async saveTailoredResume(data: {
    title: string;
    content: string;
    job_title?: string;
    company_name?: string;
    job_description?: string;
    match_score?: number;
    original_match_score?: number;
    analysis_results?: any;
    original_resume_id?: string;
    file_name?: string;
    file_type?: string;
    original_content?: string; // Add original content field
  }): Promise<TailoredResume> {
    
    // Check if a resume with the same title and job details already exists to prevent duplicates
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { data: existing } = await supabase
      .from('resumes')
      .select('id')
      .eq('user_id', user.id)
      .eq('title', data.title)
      .eq('job_title', data.job_title || '')
      .eq('company_name', data.company_name || '')
      .limit(1);

    if (existing && existing.length > 0) {
      console.log('Duplicate resume detected, skipping save:', data.title);
      // Return the existing resume instead of creating a duplicate
      const { data: existingResume } = await supabase
        .from('resumes')
        .select('*')
        .eq('id', existing[0].id)
        .single();
      return existingResume;
    }

    // First save the tailored resume to the database
    const { data: result, error } = await supabase
      .from('resumes')
      .insert({
        ...data,
        user_id: user.id,
      })
      .select()
      .single();

    if (error) {
      console.error('Error saving tailored resume:', error);
      throw error;
    }

    // Automatically generate Word document version
    try {
      console.log('Auto-generating Word document for tailored resume...');
      const wordDocumentData = await this.generateWordDocument(result);
      
      // Update the resume record with Word document URLs
      if (wordDocumentData) {
        const { data: updatedResult, error: updateError } = await supabase
          .from('resumes')
          .update({
            word_document_url: wordDocumentData.publicUrl,
            word_document_path: wordDocumentData.filename
          })
          .eq('id', result.id)
          .select()
          .single();

        if (updateError) {
          console.error('Error updating resume with Word document URLs:', updateError);
        } else {
          console.log('Word document generated successfully:', wordDocumentData.publicUrl);
          return updatedResult;
        }
      }
    } catch (error) {
      console.error('Error generating Word document:', error);
      // Don't fail the entire operation if Word generation fails
    }

    return result;
  }

  private static async generateWordDocument(resume: TailoredResume): Promise<{ publicUrl: string; filename: string } | null> {
    try {
      // Parse the resume content to extract structured data
      const documentContent = this.parseResumeContent(resume.content);
      
      // Create proper filename with person's name
      const currentDate = new Date().toLocaleString('en-US', { 
        month: 'short', 
        year: 'numeric' 
      });
      const personName = this.extractPersonName(resume.content);
      const jobTitle = resume.job_title ? ` - ${resume.job_title}` : '';
      const company = resume.company_name ? ` - ${resume.company_name}` : '';
      const customFilename = `${personName}${jobTitle}${company} - ${currentDate}`;
      
      // Generate the Word document using the edge function
      const response = await supabase.functions.invoke('generate-docx-document', {
        body: {
          title: customFilename,
          content: documentContent
        }
      });

      if (response.error) {
        throw new Error(`Word generation failed: ${response.error.message}`);
      }

      return {
        publicUrl: response.data.publicUrl,
        filename: response.data.filename
      };
    } catch (error) {
      console.error('Error in generateWordDocument:', error);
      return null;
    }
  }

  private static parseResumeContent(content: string): any {
    // Parse the text content to extract structured resume data
    const lines = content.split('\n').map(line => line.trim()).filter(line => line.length > 0);
    
    const result: any = {
      personalInfo: {},
      summary: '',
      experience: [],
      education: [],
      skills: []
    };

    let currentSection = '';
    let currentExperience: any = null;
    let currentEducation: any = null;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const lowerLine = line.toLowerCase();
      
      // Extract personal info from first few lines
      if (i === 0) {
        result.personalInfo.name = line;
      } else if (i <= 3 && (line.includes('@') || line.includes('(') || line.includes('|'))) {
        // Parse contact info
        if (line.includes('@')) {
          const emailMatch = line.match(/[\w.-]+@[\w.-]+\.\w+/);
          if (emailMatch) result.personalInfo.email = emailMatch[0];
        }
        if (line.includes('(') || line.match(/\d{3}[-.]?\d{3}[-.]?\d{4}/)) {
          const phoneMatch = line.match(/\(?(\d{3})\)?[-.]?(\d{3})[-.]?(\d{4})/);
          if (phoneMatch) result.personalInfo.phone = line.trim();
        }
        // Location is usually the rest
        const parts = line.split('|').map(p => p.trim());
        const locationPart = parts.find(p => !p.includes('@') && !p.match(/\d{3}[-.]?\d{3}[-.]?\d{4}/));
        if (locationPart) result.personalInfo.location = locationPart;
      }
      
      // Identify sections
      if (lowerLine.includes('professional summary') || lowerLine.includes('summary')) {
        currentSection = 'summary';
        continue;
      } else if (lowerLine.includes('professional experience') || lowerLine.includes('work experience') || lowerLine.includes('experience')) {
        currentSection = 'experience';
        continue;
      } else if (lowerLine.includes('education')) {
        currentSection = 'education';
        continue;
      } else if (lowerLine.includes('skills') || lowerLine.includes('technical skills')) {
        currentSection = 'skills';
        continue;
      }
      
      // Process content based on current section
      if (currentSection === 'summary' && !lowerLine.includes('summary')) {
        result.summary += (result.summary ? ' ' : '') + line;
      } else if (currentSection === 'experience') {
        // Check if this looks like a job title/company line
        if (line.match(/^[A-Z][a-zA-Z\s]+[A-Z]/) && !line.startsWith('•') && !line.startsWith('-')) {
          if (currentExperience) {
            result.experience.push(currentExperience);
          }
          // Try to parse job title and company
          const parts = line.split(/\s+at\s+|\s+\|\s+|\s+-\s+/);
          currentExperience = {
            title: parts[0] || line,
            company: parts[1] || 'Company',
            location: 'Location',
            startDate: 'Start Date',
            endDate: 'End Date',
            responsibilities: []
          };
          
          // Look for date patterns in the line or next line
          const datePattern = /(\d{4})\s*[-–]\s*(\d{4}|present|current)/i;
          const dateMatch = line.match(datePattern) || (i + 1 < lines.length ? lines[i + 1].match(datePattern) : null);
          if (dateMatch) {
            currentExperience.startDate = dateMatch[1];
            currentExperience.endDate = dateMatch[2];
          }
        } else if (currentExperience && (line.startsWith('•') || line.startsWith('-') || line.startsWith('*'))) {
          currentExperience.responsibilities.push(line.replace(/^[•\-*]\s*/, ''));
        }
      } else if (currentSection === 'education') {
        if (!line.startsWith('•') && !line.startsWith('-')) {
          if (currentEducation) {
            result.education.push(currentEducation);
          }
          currentEducation = {
            degree: line,
            school: 'University',
            location: 'Location',
            graduationDate: 'Year'
          };
        }
      } else if (currentSection === 'skills') {
        // Split skills by common separators
        const skillList = line.split(/[,•\-|]/).map(s => s.trim()).filter(s => s.length > 0);
        result.skills.push(...skillList);
      }
    }
    
    // Add final items
    if (currentExperience) {
      result.experience.push(currentExperience);
    }
    if (currentEducation) {
      result.education.push(currentEducation);
    }
    
    // Ensure we have basic personal info
    if (!result.personalInfo.email) result.personalInfo.email = 'email@example.com';
    if (!result.personalInfo.phone) result.personalInfo.phone = '(555) 123-4567';
    if (!result.personalInfo.location) result.personalInfo.location = 'City, State';
    
    return result;
  }

  static async deleteTailoredResume(id: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { error } = await supabase
      .from('resumes')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id); // CRITICAL: Only allow deletion of user's own tailored resumes

    if (error) {
      console.error('Error deleting tailored resume:', error);
      throw error;
    }
  }

  static async getTailoredResumeById(id: string): Promise<TailoredResume | null> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      .from('resumes')
      .select('*')
      .eq('id', id)
      .eq('user_id', user.id) // CRITICAL: Only allow access to user's own tailored resumes
      .single();

    if (error) {
      console.error('Error fetching tailored resume:', error);
      throw error;
    }

    return data;
  }
}